import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { t } from 'i18next';
import {
  useTable, useSortBy, useExpanded, usePagination,
} from 'react-table';
import {
  CellContainer, FiltersAndTableContainer, ReversedIcon, StyledTable, TableDataCell, TableRow, PaginationContainer, PageNumber,
} from './css';
import ChevronSmallDown from '../../../assets/icons/ChevronSmallDown.svg';
import { makeDataObject } from '../utils';
import {
  ExpandCellComponent, LocationCellComponent, OrderStatusCellComponent, StyledDropdown, TimeCellComponent,
} from './cells';
import { ShipmentStatuses } from '../../../utils/enums';
import { routes } from '../../../utils/constants';
import renderExpandables from '../../../lib/ui/table/methods';
import TertiaryButton from '../../../lib/ui/buttons/TertiaryButton';
import PaginationStart from '../../../assets/icons/PaginationStart.svg';
import PaginationStartDisabled from '../../../assets/icons/PaginationStartDisabled.svg';
import PaginationEnd from '../../../assets/icons/PaginationEnd.svg';
import PaginationEndDisabled from '../../../assets/icons/PaginationEndDisabled.svg';
import Backward from '../../../assets/icons/Backward.svg';
import BackwardDisabled from '../../../assets/icons/BackwardDisabled.svg';
import Forward from '../../../assets/icons/Forward.svg';
import ForwardDisabled from '../../../assets/icons/ForwardDisabled.svg';

function Table(props) {
  const { isAuthenticated, useOldServices, billingAccounts } = props;
  const [tablePropData, setTablePropData] = useState([]);
  const [trackingData, setTrackingData] = useState([]);
  const [dataLengthFlag, setDataLengthFlag] = useState(false);

  const { data } = props;

  useEffect(() => {
    setTablePropData(makeDataObject(props.data));
    if (data && data.length > 0 && data[0]?.orderItems?.length === 1) {
      setDataLengthFlag(true);
    } else {
      setDataLengthFlag(false);
    }
  }, [data]);

  const shipmentsRowClickHandler = (orderId, status, column, isAuthenticated, division) => {
    let shouldOpen = true;

    if (status === ShipmentStatuses.Expired || status === ShipmentStatuses.UnderReview || status === ShipmentStatuses.NotYetPickedUp) {
      shouldOpen = false;
    }

    if (column === 'actions' || column === 'expander') {
      shouldOpen = false;
    }

    if (shouldOpen) {
      if (!props.useOldServices) {
        window.open(`${routes.viewShipmentTracking}?orderId=${orderId}`);
      } else {
        window.open(`${routes.viewShipmentTracking}?division=${division}&probillNumber=${orderId}`);
      }
    }
  };

  const tableData = React.useMemo(
    () => {
      if (tablePropData) {
        return tablePropData;
      }

      return [];
    }, [tablePropData],
  );

  const isAccountAllowed = (data) => {
    const billTo = data?.row?.original?.billAccount;
    let accountAllowed = false;

    billingAccounts.map((account) => { if (parseInt(billTo, 10) === parseInt(account.accountNumber, 10)) accountAllowed = true; return null; });

    return accountAllowed;
  };

  const COLUMN_HEADERS = {
    actions: t('Actions'),
    freight: t('Freight'),
    reference: t('Reference'),
    shipper: t('shipper'),
    consignee: t('consignee'),
    estDelivery: t('EstDelivery'),
    delivered: t('Delivered'),
    status: t('Status'),
  };

  const DEFAULT_COLUMNS = [
    {
      Header: COLUMN_HEADERS.actions,
      accessor: 'actions',
      disableSortBy: true,
      Cell: function actionCell(props) {
        return StyledDropdown({
          isAuthenticated,
          useOldServices,
          division: props.data[props.row.index].division,
          probillNumber: props.data[props.row.index].orderId ? props.data[props.row.index].orderId : props.data[props.row.index].freight,
          isAccountAllowed: isAccountAllowed(props),
        });
      },
    },
    {
      Header: COLUMN_HEADERS.freight,
      accessor: 'freight',
    },
    {
      Header: COLUMN_HEADERS.reference,
      accessor: 'reference',
    },
    {
      Header: COLUMN_HEADERS.shipper,
      accessor: 'shipperAddress',
      Cell: function shipperCell(props) {
        return LocationCellComponent({ address: props.data[props.row.index].shipper });
      },
    },
    {
      Header: COLUMN_HEADERS.consignee,
      accessor: 'consigneeAddress',
      Cell: function shipperCell(props) {
        return LocationCellComponent({ address: props.data[props.row.index].consignee });
      },
    },
    {
      Header: COLUMN_HEADERS.estDelivery,
      accessor: 'estimatedDelivery',
      Cell: function deliveryCell(props) {
        return props.data[props.row.index].estimatedDelivery ? TimeCellComponent({ time: props.data[props.row.index].estimatedDelivery }) : '';
      },
    },
    {
      Header: COLUMN_HEADERS.delivered,
      accessor: 'dateDelivered',
      style: { alignContent: 'center' },
      Cell: function deliveryCell(props) {
        return props.data[props.row.index].dateDelivered ? TimeCellComponent({ time: props.data[props.row.index].dateDelivered }) : '';
      },

    },
    {
      Header: COLUMN_HEADERS.status,
      accessor: 'orderStatus',
      Cell: function orderStatusCell(props) {
        return OrderStatusCellComponent({
          percentage: props.data[props.row.index].orderStatusPercentage,
          status: props.data[props.row.index].orderStatus,
          color: props.data[props.row.index].orderStatusColor,
        });
      },
    },
    {
      Header: '',
      accessor: 'expander',
      disableSortBy: true,
      // eslint-disable-next-line react/no-unstable-nested-components
      Cell: function cellExpander({ row }) {
        return (
          <span
            {...row.getToggleRowExpandedProps({
            })}
          >
            {ExpandCellComponent({ reverse: row.isExpanded, dataLengthFlag })}
          </span>
        );
      },
    },
  ];

  const columns = React.useMemo(
    () => DEFAULT_COLUMNS,
    [],
  );

  const initialState = React.useMemo(
    () => {
      const state = { pageIndex: 0, pageSize: 10 };
      if (dataLengthFlag && tableData.length > 0) {
        state.expanded = Object.fromEntries(
          tableData.map((_, idx) => [idx, true]),
        );
      }

      return state;
    },
    [dataLengthFlag, tableData.length],
  );

  const {
    getTableProps,
    getTableBodyProps,
    headerGroups,
    prepareRow,
    visibleColumns,
    page,
    canPreviousPage,
    canNextPage,
    pageCount,
    gotoPage,
    nextPage,
    previousPage,
    state: { pageIndex },
  } = useTable(
    { columns, data: tableData, initialState },
    useSortBy,
    useExpanded,
    usePagination,
  );

  const getPaginationRange = () => {
    // Constants...
    const START_PAGE = 1;
    const DEFAULT_RANGE = 5;

    // Initializations...
    let range = DEFAULT_RANGE;

    if (range > pageCount) {
      range = pageCount;
    }

    let start = pageIndex + 1 - Math.floor(range / 2);
    start = Math.max(start, START_PAGE);
    start = Math.min(start, START_PAGE + pageCount - range);

    return Array.from(
      { length: range },
      (element, index) => start + index,
    );
  };

  return (
    <div>
      <FiltersAndTableContainer>
        {(data && data.length && pageCount > 1) ? (
          <PaginationContainer>
            <TertiaryButton
              label={t('First')}
              onClick={() => gotoPage(0)}
              size="small"
              showArrow={false}
              iconSrc={PaginationStart}
              iconSrcDisabled={PaginationStartDisabled}
              isIconLeftPosition
              disabled={!canPreviousPage}
            />
            <TertiaryButton
              type="button"
              label={t('Previous')}
              onClick={() => previousPage()}
              size="small"
              showArrow={false}
              iconSrc={Backward}
              iconSrcDisabled={BackwardDisabled}
              isIconLeftPosition
              disabled={!canPreviousPage}
            />

            {page && pageCount && getPaginationRange().map((page) => (
              <PageNumber
                type="button"
                key={page}
                selected={(pageIndex + 1 === page)}
                onClick={() => gotoPage(page - 1)}
              >
                {page}
              </PageNumber>
            ))}

            <TertiaryButton
              type="button"
              label={t('Next')}
              onClick={() => nextPage()}
              size="small"
              showArrow={false}
              iconSrc={Forward}
              iconSrcDisabled={ForwardDisabled}
              disabled={!canNextPage}
            />
            <TertiaryButton
              label={t('Last')}
              onClick={() => gotoPage(pageCount - 1)}
              size="small"
              showArrow={false}
              iconSrc={PaginationEnd}
              iconSrcDisabled={PaginationEndDisabled}
              disabled={!canNextPage}
            />
          </PaginationContainer>
        ) : null}

        <StyledTable {...getTableProps()}>
          <thead>
            {headerGroups.map((headerGroup) => (
              <tr key={headerGroup} {...headerGroup.getHeaderGroupProps()}>
                {headerGroup.headers.map((column, idx) => (
                  <th
                    key={column}
                    className={(idx < 2) ? `sticky table-header column-${idx}` : ''}
                    {...column.getHeaderProps(column.getSortByToggleProps())}
                  >
                    {column.render('Header')}
                    <span>
                      {column.isSorted
                        ? column.isSortedDesc
                          ? <img src={ChevronSmallDown} alt="sort descending" />
                          : <ReversedIcon src={ChevronSmallDown} alt="sort ascending" />
                        : ''}
                    </span>
                  </th>
                ))}
              </tr>
            ))}
          </thead>
          <tbody {...getTableBodyProps()}>
            {page.map((row) => {
              prepareRow(row);
              return (
                <React.Fragment key={row.id}>
                  <tr {...row.getRowProps()}>
                    {row.cells.map((cell, idx) => {
                      const { orderId, freight, division } = cell.row.original;
                      const orderStts = cell.row.original.orderStatus;

                      return (
                        <TableDataCell
                          topAligned
                          key={cell}
                          className={(idx < 2) ? `sticky table-cell column-${idx}` : ''}
                          {...cell.getCellProps()}
                        >
                          {cell.column.id === 'expander' ? (
                            cell.render('Cell')
                          ) : (
                            <CellContainer onClick={() =>
                              shipmentsRowClickHandler(orderId || freight, orderStts, cell.column.id, isAuthenticated, division)}
                            >
                              {cell.render('Cell')}
                            </CellContainer>
                          )}

                        </TableDataCell>
                      );
                    })}
                  </tr>
                  {row.isExpanded && (
                    <TableRow>
                      <td colSpan={visibleColumns.length}>
                        {renderExpandables('expandableTracking', row, trackingData, setTrackingData, row.original)}
                      </td>
                    </TableRow>
                  )}
                </React.Fragment>
              );
            })}
          </tbody>
        </StyledTable>
      </FiltersAndTableContainer>
    </div>
  );
}

Table.propTypes = {
  data: PropTypes.array,
  isAuthenticated: PropTypes.bool,
  useOldServices: PropTypes.bool,
  division: PropTypes.string,
  billingAccounts: PropTypes.array,
  row: PropTypes.shape({
    index: PropTypes.number,
  }),
};

export default Table;
